import * as React from 'react';
import { Carousel, type DeviceSettingType, useCarousel } from 'webapp/components/Carousel';
import { theme } from 'svenssons/shared/theme';
import { styled } from '@glitz/react';
import { useMatchDevice } from '@avensia/excite';
import { useViewEventTrigger } from 'webapp/tracking';
import { addViewItemEventToDataLayer, ListType } from 'webapp/tracking/events/items';
import { LoadingProductCard, ProductCard } from 'webapp/components/ProductCard';

type PropType = {
  id: string;
  name: string;
  products: (NordicNest.Api.PartialProductApiModel & { accessoryToSku?: string })[];
  hideBorder?: boolean;
};

const visibleCount: DeviceSettingType = { mobile: 2, tablet: 4, desktop: 4 };

export default styled(function ProductListCarousel(props: PropType) {
  const self = React.createRef<HTMLDivElement>();
  useViewEventTrigger(
    self,
    addViewItemEventToDataLayer,
    {
      products: props.products,
      list_id: props.id,
      list_name: props.name,
      list_type: ListType.ProductListBlock,
      list_pagination_index: 1,
      list_pagination_max: 1,
    },
    props.products.map(x => x.sku).join(','),
  );

  const carousel = useCarousel(props.id, { align: 'start' });

  const nextSlideFactor: DeviceSettingType = React.useMemo(
    () => ({
      mobile: props.products.length > visibleCount.mobile ? 1.15 : 1,
      tablet: props.products.length > visibleCount.tablet ? 1.15 : 1,
      desktop: 1,
    }),
    [props.products.length],
  );

  return (
    <Base ref={self}>
      <Carousel
        visibleCount={visibleCount}
        gap={theme.sharedSpacing.xs}
        showNextSlideFactor={nextSlideFactor}
        {...carousel}
      >
        {props.products?.map((product, index) => (
          <ProductCard
            key={`${product.productId} ${product.url}`}
            list_id={props.id}
            list_name={props.name}
            list_type={ListType.ProductListBlock}
            product={product}
            list_pagination_index={1}
            list_pagination_max={1}
            index={index + 1}
            hideBorder={props.hideBorder}
          />
        ))}
      </Carousel>
    </Base>
  );
});

const nextSlideFactorLoadingList: DeviceSettingType = { mobile: 1.5, tablet: 1.5, desktop: 1 };

export function LoadingProductListCarousel() {
  const deviceMatch = useMatchDevice();

  const carousel = useCarousel();

  return (
    <Base>
      <Carousel
        visibleCount={visibleCount}
        gap={deviceMatch.isMobile ? theme.sharedSpacing.m : theme.sharedSpacing.l}
        showNextSlideFactor={nextSlideFactorLoadingList}
        {...carousel}
      >
        {new Array(visibleCount.desktop).fill(1).map(({}, i) => (
          <LoadingProductCard key={i} />
        ))}
      </Carousel>
    </Base>
  );
}

const Base = styled.div();
